import { Sheet, Tooltip } from "@mui/joy";
import {
  Autocomplete,
  Box,
  Divider,
  IconButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridCellParams,
  GridColumns,
  GridPastedValueParser,
  GridRenderEditCellParams,
  GridRenderCellParams,
  GridRowId,
  GridValidRowModel,
  MuiEvent,
  useGridApiContext,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Dispatch } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  currencyFormatter,
  defaultDataGridProps,
  handleArrowNavigationForNumberCells,
  injectDefaultColumnProps,
} from "../../constants";
import { ProjectCue_Entity } from "../../entities/projectCue";
import { ProjectTask_Entity } from "../../entities/projectTask";
import { Task_Entity } from "../../entities/task";
import { Scale_Entity } from "../../entities/scale";
import { Talent_Entity } from "../../entities/talent";
import { TaskType_Entity } from "../../entities/taskType";
import { greyStyleForNonEditableCells } from "../../helpers/dataGridHelpers";
import { sortScalesByTaskType } from "../../helpers/scaleHelpers";
import { selectedProjectID } from "../../redux/filmsReducer";
import { useProject } from "../../redux/project/projectHooks";
import { useScales } from "../../redux/scale/scaleHooks";
import { useTalents } from "../../redux/talent/talentHooks";

import { useTaskTypes } from "../../redux/taskType/taskTypeHooks";
import { usePendingEdits } from "../pendingEdits/pendingEdits";
import { useSaveError } from "../saveError/saveError";
import {
  getterDivider,
  setterDivider,
} from "../jobCategory/jobCategoryRates/dataGridColDef";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import { useUpdateTaskMutation } from "../../redux/task/taskEndpoints";

type ScaleOption = {
  value: number;
  label: string;
};

/** Reads a cell through the pending-edit overlay. See `usePendingEdits`. */
type PendingValue = <T>(
  id: number | string,
  field: string,
  serverValue: T
) => T;

function parseClipboardNumber(input: string): number | undefined {
  const normalized = input.replace(/[$,%\s,]/g, "");
  if (!normalized) return undefined;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return undefined;
  return parsed;
}

function parseScaleInput(input: string): number | undefined {
  const parsed = parseClipboardNumber(input);
  if (parsed === undefined) return undefined;
  return Math.round(parsed * 10000);
}

const parsePastedNumber: GridPastedValueParser = (value) =>
  parseClipboardNumber(value);
const parsePastedScale: GridPastedValueParser = (value) =>
  parseScaleInput(value);

function ScaleAutocompleteInputCell({
  id,
  field,
  value,
  options,
}: {
  id: GridRowId;
  field: string;
  value: number;
  options: ScaleOption[];
}) {
  const apiRef = useGridApiContext();
  const selectedOption = options.find((o) => o.value === value);
  const [inputValue, setInputValue] = useState(
    selectedOption ? selectedOption.label : ""
  );

  const commitScale = async (nextScale: number) => {
    await apiRef.current.setEditCellValue({
      id,
      field,
      value: nextScale,
    });
    apiRef.current.stopCellEditMode({ id, field });
  };

  return (
    <Autocomplete<ScaleOption, false, true, true>
      freeSolo
      autoSelect
      forcePopupIcon
      autoHighlight
      openOnFocus
      fullWidth
      disableClearable
      options={options}
      PopperComponent={(props) => (
        <Popper {...props} placement="bottom-start" style={{ width: 280 }} />
      )}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_, nextInputValue) => setInputValue(nextInputValue)}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      isOptionEqualToValue={(option, current) => option.value === current.value}
      onChange={async (_, option) => {
        if (!option) return;
        if (typeof option === "string") {
          const parsed = parseScaleInput(option);
          if (parsed === undefined) return;
          await commitScale(parsed);
          return;
        }
        await commitScale(option.value);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          autoFocus
          onKeyDown={async (e) => {
            if (e.key !== "Enter") return;
            const parsed = parseScaleInput(inputValue);
            if (parsed === undefined) return;
            await commitScale(parsed);
          }}
        />
      )}
      sx={{
        width: "100%",
        "& .MuiInputBase-root": {
          fontSize: 14,
          px: 1,
        },
      }}
    />
  );
}

export default function MusicPrepSummary({
  projectTask,
}: {
  projectTask?: ProjectTask_Entity;
}) {
  const apiRef = useGridApiRef();
  const { scales } = useScales();
  const { taskTypes, taskTypesMap } = useTaskTypes();
  const { talents } = useTalents();
  const dispatch = useDispatch();
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);
  const [updateTask] = useUpdateTaskMutation();
  const { reportSaveError, saveErrorSnackbar } = useSaveError();
  const {
    pendingEdits,
    pendingValue,
    markPending,
    dropPending,
    releaseSettled,
  } = usePendingEdits();

  const tasksByID = useMemo(() => {
    const map: { [id: number]: Task_Entity } = {};
    projectTask?.cues.forEach((cue) =>
      cue.tasks.forEach((task) => {
        map[task.id] = task;
      })
    );
    return map;
  }, [projectTask]);

  // Fresh server data: let go of the cells it has caught up with.
  useEffect(() => {
    releaseSettled((id, field) => tasksByID[Number(id)]?.[field]);
  }, [tasksByID, releaseSettled]);

  const librarianScales = useMemo(
    () =>
      scales?.filter(
        (s) =>
          s.projectTypeID === project?.projectTypeID &&
          taskTypesMap?.[s.taskTypeID]?.jobCategoryID === 3
      ) ?? [],
    [project?.projectTypeID, scales, taskTypesMap]
  );

  // Save without making the grid wait. Awaiting the round-trip here holds the
  // cell in edit mode until the server answers, and anything typed in the
  // meantime is swallowed — entering a column of numbers at speed lost
  // everything after the first value. The committed value is held by
  // `markPending` so a later refetch can't revert it, and a refused save drops
  // that hold and reports itself.
  const saveTask = (taskID: number, body: { [field: string]: unknown }) => {
    Object.keys(body).forEach((field) =>
      markPending(taskID, field, body[field])
    );

    updateTask({ id: taskID, body })
      .unwrap()
      .catch((error) => {
        Object.keys(body).forEach((field) => dropPending(taskID, field));
        reportSaveError(error);
      });
  };

  const onSingleCellTaskUpdate = async (_new: any, _old: any) => {
    for (const key in _new) {
      if (Object.prototype.hasOwnProperty.call(_new, key)) {
        if (_new[key] != _old[key]) {
          // task Update
          let body = {
            [key]: _new[key],
          };
          if (key === "scale") {
            body = {
              scale: _new[key],
              rate: _new[key],
            };
          }
          saveTask(_new.taskID, body);
        }
      }
    }

    return { ..._new };
  };

  const onMultipleCellTaskUpdate = async (_new: any, _old: any) => {
    for (const taskTypeID in _new) {
      if (Object.prototype.hasOwnProperty.call(_new, taskTypeID)) {
        const cell = _new[taskTypeID];
        // Only the task-type columns hold {taskID, field, value} cells; name,
        // id, total and bars are plain values on the same row.
        if (!cell || typeof cell !== "object" || !cell.taskID) continue;
        if (cell.value != _old[taskTypeID]?.value) {
          saveTask(cell.taskID, { [cell.field]: cell.value });
        }
      }
    }

    return { ..._new };
  };

  if (!projectTask) return <div />;

  const multipleCell = projectTask.id === 2;
  const handleCellKeyDown = async (
    params: GridCellParams,
    event: MuiEvent<any>
  ) => {
    handleArrowNavigationForNumberCells(params, event);

    const target = event.target as HTMLElement | null;
    const key = event.key.toLowerCase();
    const isInputTarget =
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement;
    const isSpinbutton = target?.getAttribute("role") === "spinbutton";
    const isTextInputTarget = isInputTarget || isSpinbutton;
    const isNumberField = params.colDef.type === "number";

    if (
      key === "enter" &&
      params.isEditable &&
      isNumberField &&
      isTextInputTarget &&
      isInputTarget &&
      target.value.trim() === ""
    ) {
      event.preventDefault();
      event.stopPropagation();
      await apiRef.current.setEditCellValue({
        id: params.id,
        field: params.field,
        value: 0,
      });
      apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
    }
  };

  const dataGrid = useMemo(() => {
    return (
      <DataGridPro
        {...defaultDataGridProps}
        onCellKeyDown={handleCellKeyDown}
        getCellClassName={(p) => {
          if (project?.locked()) return "lock";
          const classes = greyStyleForNonEditableCells(p);
          return classes;
        }}
        isCellEditable={(params) =>
          project?.locked() ? false : params.colDef.editable ?? false
        }
        apiRef={apiRef}
        processRowUpdate={
          multipleCell ? onMultipleCellTaskUpdate : onSingleCellTaskUpdate
        }
        onProcessRowUpdateError={reportSaveError}
        rows={
          multipleCell
            ? getRowsForMultipleCell(
                projectTask.cues,
                scales ?? [],
                taskTypes ?? [],
                pendingValue
              )
            : getRowsForSingleCell(projectTask.cues, pendingValue)
        }
        columns={
          multipleCell
            ? getColumnsForMultipleCell(
                projectTask?.cues[0],
                scales ?? [],
                taskTypes ?? [],
                dispatch
              )
            : getColumnsForSingleCell(
                talents ?? [],
                librarianScales,
                taskTypesMap ?? {}
              )
        }
        slots={{
          noRowsOverlay: NoRowsOverlay,
          footer: MusicPrepSummaryFooter,
        }}
        slotProps={{
          footer: { projectTask },
          cell: { tabIndex: 1 },
        }}
        initialState={{ pinnedColumns: { left: ["name"], right: ["total"] } }}
      />
    );
  }, [
    project,
    projectTask,
    multipleCell,
    scales,
    taskTypes,
    taskTypesMap,
    talents,
    librarianScales,
    pendingEdits,
  ]);

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {dataGrid}
      {saveErrorSnackbar}
    </Paper>
  );
}

function MusicPrepSummaryFooter(props: { projectTask: ProjectTask_Entity }) {
  return (
    <>
      <Divider />
      <Box sx={{ p: 2, textAlign: "right", fontWeight: 600 }}>
        {props.projectTask.id === 3 ? "Librarian: " : ""}
        {props.projectTask.id === 2 ? "Subtotal (no lib): " : ""}
        {currencyFormatter.format(props.projectTask.subTotal / 10000)}
      </Box>
    </>
  );
}

const getRowsForSingleCell = (
  cues: ProjectCue_Entity[],
  pendingValue: PendingValue
): GridValidRowModel[] => {
  const rows = [];

  for (const k in cues) {
    if (Object.prototype.hasOwnProperty.call(cues, k)) {
      const cue = cues[k];
      const task = cue.tasks[0];
      const row = {
        name: cue.name,
        bars: cue.bars,
        id: cue.id,
        quantity: pendingValue(task.id, "quantity", task.quantity),
        rate: pendingValue(task.id, "rate", task.rate),
        scale: pendingValue(task.id, "scale", task.scale),
        taskID: task.id,
        total: cue.total,
      };

      rows.push(row);
    }
  }

  return rows;
};

const getRowsForMultipleCell = (
  cues: ProjectCue_Entity[],
  scales: Scale_Entity[],
  taskTypes: TaskType_Entity[],
  pendingValue: PendingValue
): GridValidRowModel[] => {
  const rows = [];

  for (const k in cues) {
    if (Object.prototype.hasOwnProperty.call(cues, k)) {
      const cue = cues[k];
      if (!cue.showInMPS) continue;
      const row = {
        name: cue.name,
        id: cue.id,
        total: cue.total,
        bars: cue.bars,
      };

      for (const j in cue.tasks) {
        if (Object.prototype.hasOwnProperty.call(cue.tasks, j)) {
          const task = cue.tasks[j];
          const scale = scales.find((s) => s.id === task.scaleID);
          const taskType = taskTypes.find((t) => t.id === scale?.taskTypeID);
          row[`${taskType?.id}`] = {
            value: pendingValue(task.id, "quantity", task.quantity),
            taskID: task.id,
            field: "quantity",
            rate: task.rate,
          };
        }
      }

      rows.push(row);
    }
  }

  return rows;
};

const getColumnsForSingleCell = (
  talents: Talent_Entity[],
  librarianScales: Scale_Entity[],
  taskTypes: {
    [key: number]: TaskType_Entity;
  }
): GridColumns => {
  const scaleOptions = getOptions(librarianScales, taskTypes);
  const cols: GridColumns = [
    {
      field: "name",
      headerName: "Cue",
      headerAlign: "center",
      editable: false,
      sortable: false,
      type: "string",
      width: 150,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "space-between" }}>
          <Typography variant="body2">{p.value}</Typography>
        </Box>
      ),
    },
    {
      field: "quantity",
      headerName: "Quantity",
      headerAlign: "center",
      sortable: false,
      editable: true,
      type: "number",
      pastedValueParser: parsePastedNumber,
      valueGetter: getterDivider("quantity", 100),
      valueSetter: setterDivider("quantity", 100),
    },
    {
      field: "rate",
      headerName: "Rate",
      headerAlign: "center",
      sortable: false,
      editable: true,
      type: "number",
      pastedValueParser: parsePastedNumber,
      valueGetter: getterDivider("rate", 10000),
      valueSetter: setterDivider("rate", 10000),
      valueFormatter: (valueOrParams: any) => {
        const value = valueOrParams?.value ?? valueOrParams;
        return `${currencyFormatter.format(value)}`.substring(1);
      },
    },
    {
      field: "scale",
      headerName: "Scale",
      headerAlign: "center",
      align: "right",
      sortable: false,
      editable: true,
      type: "singleSelect",
      valueOptions: scaleOptions,
      pastedValueParser: parsePastedScale,
      renderEditCell: (p: GridRenderEditCellParams<number>) => (
        <ScaleAutocompleteInputCell
          id={p.id}
          field={p.field}
          value={p.row.scale}
          options={scaleOptions}
        />
      ),
      valueGetter: getterDivider("scale", 10000),
      valueFormatter: (valueOrParams: any) => {
        const value = valueOrParams?.value ?? valueOrParams;
        return `${currencyFormatter.format(value)}`.substring(1);
      },
    },
    {
      field: "total",
      headerAlign: "center",
      headerName: "Total",
      sortable: false,
      editable: false,
      type: "number",
      valueGetter: getterDivider("total", 10000),
      valueSetter: setterDivider("total", 10000),
      valueFormatter: (valueOrParams: any) => {
        const value = valueOrParams?.value ?? valueOrParams;
        return `${currencyFormatter.format(value)}`.substring(1);
      },
    },
  ];

  return injectDefaultColumnProps(cols);
};

const getColumnsForMultipleCell = (
  cue: ProjectCue_Entity,
  scales: Scale_Entity[],
  taskTypes: TaskType_Entity[],
  dispatch: Dispatch
): GridColumns => {
  const cols: GridColumns = [
    {
      field: "name",
      headerName: "Cue",
      headerAlign: "center",
      sortable: false,
      editable: false,
      type: "string",
      width: 150,
      renderCell: (p) => {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flex: 1,
              width: "100%",
              alignItems: "center",
            }}
          >
            <Typography variant="body2">{p.value}</Typography>
            <Tooltip size="sm" placement="right" title="Bars">
              <Sheet
                variant="solid"
                sx={{
                  p: 0.5,
                  background: "#9e9e9e",
                  borderRadius: 4,
                  height: 16,
                  fontSize: 12,
                  display: "flex",
                  alignItems: "center",
                  position: "sticky",
                  right: 0,
                }}
              >
                {p.row.bars}
              </Sheet>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  if (!cue) return [];

  for (const k in cue.tasks) {
    if (Object.prototype.hasOwnProperty.call(cue.tasks, k)) {
      const task = cue.tasks[k];
      const scale = scales.find((s) => s.id === task.scaleID);
      const taskType = taskTypes.find((t) => t.id === scale?.taskTypeID);

      cols.push({
        field: `${taskType?.id}`,
        editable: true,
        headerName: `${taskType?.name}`,
        type: "number",
        sortable: false,
        align: "center",
        headerAlign: "center",
        valueFormatter: (valueOrParams: any) => {
          const value = valueOrParams?.value ?? valueOrParams;
          return value ?? "";
        },
        pastedValueParser: parsePastedNumber,
        valueSetter: (value: any, row: any) => {
          if (value === "" || value === null || value === undefined) {
            const n = {
              ...row[`${taskType?.id}`],
              value: 0,
            };
            return { ...row, [`${taskType?.id}`]: n };
          }
          const parsed =
            typeof value === "string"
              ? parseClipboardNumber(value)
              : Number(value);
          if (!Number.isFinite(parsed)) return row;
          const n = {
            ...row[`${taskType?.id}`],
            value: Math.round(parsed * 100),
          };
          return { ...row, [`${taskType?.id}`]: n };
        },
        renderCell: (p) => <RenderCell p={p} scale={scale} />,
        valueGetter: (_value: any, row: any) =>
          (row[`${taskType?.id}`]?.value ?? 0) / 100,
        renderHeader: () => (
          <Box
            sx={{
              lineHeight: 0.8,
              textAlign: "center",
            }}
          >
            <Typography variant="body2">
              {taskType?.name.toUpperCase()}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {`${currencyFormatter.format(
                (scale?.rate ?? 0) / 10000
              )}`.substring(1)}
            </Typography>
          </Box>
        ),
      });
    }
  }

  cols.push({
    field: "total",
    headerAlign: "center",
    headerName: "Total",
    editable: false,
    sortable: false,
    type: "number",
    valueGetter: getterDivider("total", 10000),
    valueSetter: setterDivider("total", 10000),
    valueFormatter: (valueOrParams: any) => {
      const value = valueOrParams?.value ?? valueOrParams;
      return `${currencyFormatter.format(value)}`.substring(1);
    },
  });

  return injectDefaultColumnProps(cols);
};

const RenderCell = ({
  p,
  scale,
}: {
  p: GridRenderCellParams;
  scale?: Scale_Entity;
}) => {
  const [spin, setSpin] = useState(false);
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);
  const [updateTask] = useUpdateTaskMutation();
  const taskCell = p.row?.[p.field];

  const handleReconcileRate = (p: GridRenderCellParams, rate: number) => {
    if (!taskCell?.taskID) return;
    setSpin(true);
    const taskID = taskCell.taskID;

    updateTask({
      id: taskID,
      body: {
        rate,
      },
    });
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {taskCell?.rate !== scale?.rate && !project?.locked() ? (
        <Tooltip
          title={
            <div style={{ textAlign: "center", fontSize: 12 }}>
              Rate is not up-to-date:{" "}
              {currencyFormatter.format((taskCell?.rate ?? 0) / 10000)} instead
              of {currencyFormatter.format((scale?.rate ?? 0) / 10000)}
              <br />
              Click to reconcile locally or click on the{" "}
              <b>
                <u>Use latest rates</u>
              </b>{" "}
              button.
            </div>
          }
        >
          <IconButton
            onClick={() => handleReconcileRate(p, scale?.rate ?? 0)}
            size="small"
          >
            <i
              style={{ fontSize: 16, color: "#ff9800" }}
              className={`fa-solid fa-arrows-rotate ${spin ? "fa-spin" : ""}`}
            ></i>
          </IconButton>
        </Tooltip>
      ) : (
        []
      )}
      <Typography variant="body2">{p.value > 0 ? p.value : ""}</Typography>
    </Box>
  );
};

export type MultipleCell = {
  taskID: number;
  value: number;
  field: string;
};

function getOptions(
  items: Scale_Entity[],
  taskTypes: { [key: number]: TaskType_Entity }
): ScaleOption[] {
  const res: ScaleOption[] = [];
  sortScalesByTaskType(items, taskTypes).forEach((item) => {
    res.push({
      value: item.rate,
      label: `${taskTypes[item.taskTypeID]?.name}: ${currencyFormatter.format(
        item.rate / 10000
      )}`,
    });
  });

  return res;
}
